import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';
import { riskColors } from 'utils/constants/risks';
import Map from 'components/Map';
import styles from './index.module.scss';
import cn from 'classnames';
import FormHelperText from '@material-ui/core/FormHelperText';
import {
  DEFAULT_ZOOM_LEVEL,
  DISTANCE,
  DrawingOptions,
  HIGH_DETAIL_CONFIG,
  RADIUS,
} from 'utils/constants/Map';
import DrawingMenuOptions from 'components/Map/MapOptions/DrawingMenu';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { useTranslation } from 'react-i18next';

const MapPolygon = ({
  label,
  primaryPolygon,
  onChange,
  required,
  eventType,
  error,
  info,
  secondaryPolygons = [],
  mapCentre,
}) => {
  const dragRef = useRef(null);
  const { t } = useTranslation();
  const [drawingStyle, setDrawingStyle] = useState(DrawingMenuOptions.marker);

  const checkDrawingStyle = useCallback(() => {
    if (primaryPolygon.length > 0 && primaryPolygon[0].isFilled) {
      setDrawingStyle(DrawingOptions.polygon);
    } else if (primaryPolygon[0] && primaryPolygon[0].center) {
      setDrawingStyle(DrawingOptions.ellipse);
    } else {
      setDrawingStyle(DrawingOptions.marker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkDrawingStyle();
  }, [checkDrawingStyle]);

  const onMapClick = (evt, map) => {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();

    if (drawingStyle === DrawingOptions.rectangle) {
      const number = DISTANCE[map.getZoom()] || DISTANCE[DEFAULT_ZOOM_LEVEL];

      const northEast = {
        lat: lat + number,
        lng: lng - number,
      };
      const northWest = {
        lat: lat + number,
        lng: lng + number,
      };

      const southEast = {
        lat: lat - number,
        lng: lng - number,
      };
      const southWest = {
        lat: lat - number,
        lng: lng + number,
      };

      onChange([
        {
          editable: true,
          draggable: true,
          isFilled: true,
          color: riskColors[eventType],
          paths: [northEast, northWest, southWest, southEast],
          strokeOpacity: HIGH_DETAIL_CONFIG.strokeOpacity,
          strokeWeight: HIGH_DETAIL_CONFIG.strokeWeight,
        },
      ]);
    } else if (drawingStyle === DrawingOptions.ellipse) {
      const lat = evt.latLng.lat();
      const lng = evt.latLng.lng();
      const radius = RADIUS[map.getZoom()] || RADIUS[DEFAULT_ZOOM_LEVEL];
      onChange([
        {
          center: {
            lat,
            lng,
            isWaypoint: false,
            radius: 0,
          },
          radiusX: radius.x,
          radiusY: radius.y,
          rotationAngle: 0,
          draggable: true,
          color: riskColors[eventType],
          editable: true,
          strokeOpacity: HIGH_DETAIL_CONFIG.strokeOpacity,
          strokeWeight: HIGH_DETAIL_CONFIG.strokeWeight,
        },
      ]);
    } else if (drawingStyle === DrawingOptions.polygon) {
      if (primaryPolygon.length > 0) {
        const [valueClone] = primaryPolygon;
        onChange([
          {
            editable: true,
            draggable: false,
            isFilled: true,
            color: riskColors[eventType],
            strokeOpacity: HIGH_DETAIL_CONFIG.strokeOpacity,
            strokeWeight: HIGH_DETAIL_CONFIG.strokeWeight,
            paths: valueClone.paths.concat({ lat, lng }),
          },
        ]);
      } else {
        onChange([
          {
            editable: true,
            draggable: false,
            isFilled: true,
            color: riskColors[eventType],
            paths: [{ lat, lng }],
          },
        ]);
      }
    } else {
      onChange(
        primaryPolygon.concat({
          lat,
          lng,
          seqNumber: primaryPolygon.length,
          icon: eventType.toLowerCase(),
        }),
      );
    }
  };

  const markerDragStart = evt => {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();
    const dragged = primaryPolygon.find((marker, index) => {
      if (marker.lat === lat && marker.lng === lng) {
        return { ...marker, seqNumber: index };
      }
      return undefined;
    });
    dragRef.current = { ...dragged };
  };

  const markerDragEnd = evt => {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();
    const markersClone = primaryPolygon;

    markersClone[dragRef.current.seqNumber] = {
      ...dragRef.current,
      lat,
      lng,
    };
    onChange([...markersClone]);
  };

  const clear = () => {
    setDrawingStyle(DrawingOptions.marker);
    onChange();
  };

  const setValues = vals => {
    onChange(vals);
  };

  function updateStyle(style) {
    setDrawingStyle(style);
    onChange([]);
  }

  const menuOptions = [
    {
      type: DrawingOptions.marker,
      onClick: () => updateStyle(DrawingOptions.marker),
    },
    {
      type: DrawingOptions.polygon,
      onClick: () => updateStyle(DrawingOptions.polygon),
    },
    {
      type: DrawingOptions.rectangle,
      onClick: () => updateStyle(DrawingOptions.rectangle),
    },
    {
      type: DrawingOptions.ellipse,
      onClick: () => updateStyle(DrawingOptions.ellipse),
    },
  ];

  const getDrawingStyleProps = () => {
    const combineSecondaryPolygons = [...primaryPolygon, ...secondaryPolygons];
    switch (drawingStyle) {
      case DrawingOptions.rectangle:
        return {
          rectangles: combineSecondaryPolygons,
        };
      case DrawingOptions.ellipse:
        return { ellipses: combineSecondaryPolygons };

      case DrawingOptions.polygon:
        return { polygons: combineSecondaryPolygons };

      default:
        return { polygons: combineSecondaryPolygons };
    }
  };

  return (
    <div className={styles.panelContainer}>
      <InputLabel
        shrink
        className={styles.label}
        required={required}
        error={error}
      >
        {t(label)}
        {info && <Tooltip text={info} />}
      </InputLabel>
      <div
        className={cn(
          styles.mapContainer,
          // HACK - simpler to get this working by attaching the class name
          // instead of using FormControl class - it messes up the layout
          error ? 'MuiInput-underline Mui-error' : '',
        )}
      >
        <Map
          markers={drawingStyle === 'MARKER' ? primaryPolygon : []}
          mapInForm={true}
          polygonsInForm={true} // it will disable the popup and mark the actual lat lng position
          onMapClick={onMapClick}
          markerDragStart={markerDragStart}
          markerDragEnd={markerDragEnd}
          markerDraggable={true}
          draggable={false}
          editable={true}
          clear={clear}
          setValues={setValues}
          isZoomEnabled={false}
          center={mapCentre}
          customMapControl={
            <DrawingMenuOptions
              options={menuOptions}
              onClear={clear}
              defaultDrawingTool={drawingStyle}
            />
          }
          {...getDrawingStyleProps()}
        />
      </div>
      <FormHelperText
        style={error ? { opacity: 1 } : { opacity: 0 }}
        className={error ? 'MuiFormHelperText-root Mui-error' : ''}
        id="component-error-text"
      >
        {t('required_label')}
      </FormHelperText>
    </div>
  );
};

MapPolygon.propTypes = {
  label: PropTypes.string,
  primaryPolygon: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  eventType: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  info: InfoPropTypes,
  secondaryPolygons: PropTypes.array,
  mapCentre: PropTypes.object,
};

MapPolygon.defaultProps = {
  primaryPolygon: [],
};

export default MapPolygon;
